import { useState, useMemo } from 'react';
import {
  Mail,
  Plus,
  Users,
  Search,
  Clock,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';
import { SyncedSharedMailbox, SyncCategory } from '../../types';
import CreateSharedMailboxModal from './CreateSharedMailboxModal';
import SharedMailboxDetail from './SharedMailboxDetail';
import { formatSyncTime } from '../../hooks/useClientSyncData';

interface SharedMailboxListProps {
  clientId: string;
  clientName: string;
  tenantId: string | null;
  // Sync data passed from parent
  syncData?: SyncCategory<SyncedSharedMailbox>;
  onRefreshNeeded?: () => void;
}

export default function SharedMailboxList({
  clientId,
  clientName,
  tenantId,
  syncData,
  onRefreshNeeded
}: SharedMailboxListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMailbox, setSelectedMailbox] = useState<SyncedSharedMailbox | null>(null);

  // Get mailboxes from sync data
  const mailboxes = syncData?.data || [];
  const lastSynced = syncData?.lastSynced;
  const syncStatus = syncData?.status;
  const syncError = syncData?.error;

  // Filter mailboxes by search term
  const filteredMailboxes = useMemo(() => {
    if (!searchTerm) return mailboxes;
    const term = searchTerm.toLowerCase();
    return mailboxes.filter(
      (mb) =>
        mb.displayName.toLowerCase().includes(term) ||
        mb.email.toLowerCase().includes(term) ||
        mb.alias.toLowerCase().includes(term)
    );
  }, [mailboxes, searchTerm]);

  // No tenant configured
  if (!tenantId) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
        <p className="text-yellow-700 dark:text-yellow-300">
          No Microsoft tenant ID configured. Please link this client to a tenant to manage shared mailboxes.
        </p>
      </div>
    );
  }

  // Never synced
  if (!syncData || syncStatus === 'pending') {
    return (
      <div className="text-center py-12 bg-muted rounded-lg">
        <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">
          Data Not Yet Synced
        </h3>
        <p className="text-muted-foreground mb-4">
          Click "Sync Now" above to fetch shared mailboxes from Exchange Online.
        </p>
      </div>
    );
  }

  // Sync failed
  if (syncStatus === 'failed' && mailboxes.length === 0) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-red-800 dark:text-red-200">Failed to sync mailboxes</h3>
            <p className="text-red-700 dark:text-red-300 text-sm mt-1">
              {syncError || 'An unknown error occurred'}
            </p>
            <p className="text-red-600 dark:text-red-400 text-xs mt-2">
              Click "Sync Now" above to try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with search and create */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search mailboxes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          {lastSynced && (
            <span className="text-xs text-muted-foreground">
              Updated {formatSyncTime(lastSynced)}
            </span>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Create Shared Mailbox
          </button>
        </div>
      </div>

      {/* Empty State */}
      {mailboxes.length === 0 && syncStatus === 'success' && (
        <div className="text-center py-12 bg-muted rounded-lg">
          <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No shared mailboxes
          </h3>
          <p className="text-muted-foreground mb-4">
            Create a shared mailbox to get started.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Create Shared Mailbox
          </button>
        </div>
      )}

      {/* No search results */}
      {mailboxes.length > 0 && filteredMailboxes.length === 0 && searchTerm && (
        <div className="text-center py-12 bg-muted rounded-lg">
          <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No mailboxes found
          </h3>
          <p className="text-muted-foreground">
            Try adjusting your search terms.
          </p>
        </div>
      )}

      {/* Mailbox List */}
      {filteredMailboxes.length > 0 && (
        <div className="bg-card shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Mailbox
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Members
                </th>
                <th className="px-3 py-3 w-8">
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredMailboxes.map((mailbox) => (
                <tr
                  key={mailbox.id}
                  className="hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => setSelectedMailbox(mailbox)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-foreground">
                          {mailbox.displayName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Alias: {mailbox.alias}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-foreground">{mailbox.email}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-foreground">
                      <Users className="h-4 w-4 mr-1.5 text-muted-foreground" />
                      {mailbox.memberCount} {mailbox.memberCount === 1 ? 'member' : 'members'}
                    </div>
                    {mailbox.members.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1 truncate max-w-xs" title={mailbox.members.join(', ')}>
                        {mailbox.members.slice(0, 2).join(', ')}
                        {mailbox.members.length > 2 && ` +${mailbox.members.length - 2} more`}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-4 text-muted-foreground">
                    <ChevronRight className="h-4 w-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Count */}
      {filteredMailboxes.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Showing {filteredMailboxes.length} of {mailboxes.length} shared mailboxes
        </p>
      )}

      {/* Create Modal */}
      <CreateSharedMailboxModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        clientId={clientId}
        clientName={clientName}
        onCreated={() => {
          setShowCreateModal(false);
          // Trigger a refresh of sync data
          if (onRefreshNeeded) {
            onRefreshNeeded();
          }
        }}
      />

      {/* Detail Panel */}
      {selectedMailbox && (
        <SharedMailboxDetail
          isOpen={!!selectedMailbox}
          onClose={() => setSelectedMailbox(null)}
          clientId={clientId}
          mailbox={selectedMailbox}
          onDeleted={() => {
            setSelectedMailbox(null);
            if (onRefreshNeeded) {
              onRefreshNeeded();
            }
          }}
        />
      )}
    </div>
  );
}
